import useSWR, { KeyedMutator } from "swr"

import {
    ApplicationStatuses,
    DnoEscalationStatuses,
} from "../../../../../constants/enums"
import { postClientProtected } from "../../../clients/clients"
import {
    ISingleApplicationDetail,
    dnoApplicationsMapper,
} from "../../../mappers/dnoInterface/dnoApplicationsMapper"
import { DNO_SINGLE_APPLICATION } from "../../../urls/hybrid"

export type TGetSingleApplication = {
    resMapped: ISingleApplicationDetail | undefined
    resDownload: unknown
}

interface IDnoApplication {
    application: ISingleApplicationDetail | undefined
    downloadData: unknown
    isLoading: boolean
    isValidating: boolean
    isError: boolean
    mutate: KeyedMutator<TGetSingleApplication>
    isEscalation: boolean | undefined
}

/**
 * Hook for getting a single connection application, viewed as a DNO
 * parameter for this hook. Note this returns both the mapped and raw response.
 * The raw response is used for downloading the application.
 *
 * @param {string} applicationId - the ID of the application
 * @returns {IUseMultiFetcherGetDevice} - SWR data, loading and error states.
 */
const useDnoSingleApplication = (applicationId: string): IDnoApplication => {
    const { data, error, isLoading, isValidating, mutate } = useSWR(
        DNO_SINGLE_APPLICATION(applicationId),
        async (url: string): Promise<TGetSingleApplication> => {
            try {
                const res = await postClientProtected(url, null)
                const resMapped = dnoApplicationsMapper.mapSingle(res)
                const resDownload =
                    dnoApplicationsMapper.mapSingleForDownload(res)
                return { resMapped, resDownload }
            } catch {
                throw Error()
            }
        }
    )
    const resMapped = data?.resMapped
    return {
        application: data?.resMapped,
        downloadData: data?.resDownload,
        isLoading,
        isValidating,
        isError: error,
        mutate,
        isEscalation: resMapped
            ? resMapped.applicationStatus ===
                  DnoEscalationStatuses.escalatedToDno ||
              resMapped.applicationStatus ===
                  DnoEscalationStatuses.reEscalatedToDno ||
              resMapped.applicationStatus ===
                  ApplicationStatuses.dnoWorksRequired
            : undefined,
    }
}

export default useDnoSingleApplication
